import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  HardHat, Plus, RefreshCw, Trash2, Eye, CheckCircle2, Clock,
  Loader2, Search, X, Trash, Package, FileUp, Building2,
  FileSpreadsheet,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import BulkDocsUploadDialog from "./BulkDocsUploadDialog";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); }
  catch { return iso; }
};

function BulkEpiContractDialog({ open, onOpenChange, onDone, adminToken }) {
  const [contracts, setContracts] = useState([]);
  const [contract, setContract] = useState("");
  const [cidade, setCidade] = useState("Salvador/BA");
  const [items, setItems] = useState([
    { data_entrega: "", qtd: 1, descricao: "", ca: "" },
  ]);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const headers = { Authorization: `Bearer ${adminToken}` };

  useEffect(() => {
    if (!open) return;
    setJob(null);
    setContract("");
    axios.get(`${API}/admin/kit-documents/contracts`,
      { headers, timeout: 30000 })
      .then((r) => setContracts(r.data?.items || []))
      .catch(() => toast.error("Falha ao carregar contratos"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!job?.job_id || job.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/admin/kit-documents/bulk-jobs/${job.job_id}`, { headers });
        setJob((prev) => ({ ...prev, ...r.data }));
        if (r.data.status !== "running") {
          clearInterval(t);
          toast.success(
            `Fichas emitidas: ${r.data.done} — ${r.data.skipped} já tinham`);
          if (typeof onDone === "function") onDone();
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, job?.status]);

  const updateItem = (idx, field, value) =>
    setItems((prev) => prev.map((it, i) =>
      i === idx ? { ...it, [field]: value } : it));
  const addItem = () =>
    setItems((prev) => [...prev, { data_entrega: "", qtd: 1, descricao: "", ca: "" }]);
  const removeItem = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const start = async () => {
    if (!items.some((it) => (it.descricao || "").trim())) {
      toast.error("Informe ao menos 1 EPI");
      return;
    }
    setStarting(true);
    try {
      const r = await axios.post(`${API}/admin/epi-records/bulk-create`,
        { contract, items, cidade_assinatura: cidade }, { headers });
      setJob({ job_id: r.data.job_id, total: r.data.total,
        done: 0, skipped: 0, errors: 0, status: "running" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar emissão");
    } finally {
      setStarting(false);
    }
  };

  const processed = job
    ? job.done + job.skipped + (job.errors || 0) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o && job?.status === "running") return;
      onOpenChange(o);
    }}>
      <DialogContent className="max-w-3xl w-[95vw]" data-testid="epi-contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-700" />
            Emitir Ficha de EPI por contrato
          </DialogTitle>
          <DialogDescription>
            A ficha (com os mesmos EPIs) será gerada para TODOS os
            funcionários do contrato como pendência obrigatória no portal.
            Quem já possui ficha é pulado.
          </DialogDescription>
        </DialogHeader>

        {!job ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Contrato</Label>
              <Select value={contract} onValueChange={setContract}>
                <SelectTrigger data-testid="epi-contract-select">
                  <SelectValue placeholder="Selecione o contrato…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {contracts.map((c) => (
                    <SelectItem key={c.contract} value={c.contract}>
                      {c.contract} ({c.count} funcionário{c.count > 1 ? "s" : ""})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Package className="w-3.5 h-3.5" />
                  EPIs Entregues (iguais para todos)
                </Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem}
                  data-testid="epi-contract-add-item">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                </Button>
              </div>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx}
                    className="grid grid-cols-12 gap-2 items-end pb-2 border-b border-slate-100 last:border-0">
                    <div className="col-span-3">
                      <Label className="text-[10px] uppercase">Data entrega</Label>
                      <Input className="text-xs" placeholder="DD/MM/AAAA"
                        value={it.data_entrega}
                        onChange={(e) => updateItem(idx, "data_entrega", e.target.value)}
                        data-testid={`epi-contract-item-${idx}-date`} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-[10px] uppercase">Qtd</Label>
                      <Input className="text-xs" type="number" min="1"
                        value={it.qtd}
                        onChange={(e) => updateItem(idx, "qtd", e.target.value)} />
                    </div>
                    <div className="col-span-5">
                      <Label className="text-[10px] uppercase">EPI fornecido</Label>
                      <Input className="text-xs" placeholder="Ex: CAMISA MANGA COMPRIDA"
                        value={it.descricao}
                        onChange={(e) => updateItem(idx, "descricao", e.target.value)}
                        data-testid={`epi-contract-item-${idx}-desc`} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase">C.A.</Label>
                      <Input className="text-xs" placeholder="49490"
                        value={it.ca}
                        onChange={(e) => updateItem(idx, "ca", e.target.value)} />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button type="button" variant="ghost" size="sm"
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}>
                        <Trash className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Cidade da assinatura</Label>
              <Input value={cidade} onChange={(e) => setCidade(e.target.value)}
                placeholder="Salvador/BA" />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={start} disabled={!contract || starting}
                className="bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="epi-contract-start">
                {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <Building2 className="w-4 h-4 mr-2" />}
                Emitir para{" "}
                {contracts.find((c) => c.contract === contract)?.count || ""}{" "}
                funcionários
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="epi-contract-progress">
            <div className="text-sm font-medium text-slate-800">
              {job.status === "running" ? "Emitindo fichas…"
                : job.status === "finished" ? "Emissão concluída!"
                : "Emissão com erro"}
            </div>
            <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-amber-600 transition-all"
                style={{ width: `${Math.min(100,
                  Math.round((processed / (job.total || 1)) * 100))}%` }} />
            </div>
            <div className="text-xs text-slate-600">
              {processed} de {job.total} processados — {job.done} emitidas,{" "}
              {job.skipped} já tinham
              {job.errors ? `, ${job.errors} erro(s)` : ""}
            </div>
            {job.status !== "running" && (
              <DialogFooter>
                <Button onClick={() => onOpenChange(false)}
                  data-testid="epi-contract-close">
                  Fechar
                </Button>
              </DialogFooter>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BulkEpiExcelDialog({ open, onOpenChange, onDone, adminToken }) {
  const [fileName, setFileName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [cidade, setCidade] = useState("Salvador/BA");
  const [items, setItems] = useState([{ qtd: 1, descricao: "", ca: "" }]);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const headers = { Authorization: `Bearer ${adminToken}` };

  useEffect(() => {
    if (!open) return;
    setFileName(""); setResolved(null); setJob(null);
    setItems([{ qtd: 1, descricao: "", ca: "" }]);
    setCidade("Salvador/BA");
  }, [open]);

  useEffect(() => {
    if (!job?.job_id || job.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/admin/kit-documents/bulk-jobs/${job.job_id}`, { headers });
        setJob((prev) => ({ ...prev, ...r.data }));
        if (r.data.status !== "running") {
          clearInterval(t);
          toast.success(
            `Fichas emitidas: ${r.data.done} — ${r.data.skipped} já tinham`);
          if (typeof onDone === "function") onDone();
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, job?.status]);

  const parseFile = async (file) => {
    setFileName(file.name);
    setResolving(true);
    setResolved(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, {
        header: 1, raw: false, defval: "",
      });
      // Detect header row (matricula / nome) or fallback heuristic
      let matCol = -1, nomeCol = -1, startRow = 0;
      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const r = rows[i].map((c) => String(c || "").toLowerCase());
        const m = r.findIndex((c) => c.includes("matr"));
        const n = r.findIndex((c) => c.includes("nome"));
        if (m >= 0 || n >= 0) {
          matCol = m; nomeCol = n; startRow = i + 1;
          break;
        }
      }
      const entries = [];
      for (let i = startRow; i < rows.length; i++) {
        const r = rows[i] || [];
        let matricula = "", nome = "";
        if (matCol >= 0 || nomeCol >= 0) {
          matricula = matCol >= 0 ? String(r[matCol] || "").trim() : "";
          nome = nomeCol >= 0 ? String(r[nomeCol] || "").trim() : "";
        } else {
          for (const cell of r) {
            const v = String(cell || "").trim();
            if (!v) continue;
            if (/^\d{1,10}$/.test(v.replace(/\D/g, "")) && /\d/.test(v) && !/[a-zA-Z]{2,}/.test(v)) {
              if (!matricula) matricula = v;
            } else if (/[a-zA-ZÀ-ú]{2,}/.test(v)) {
              if (!nome) nome = v;
            }
          }
        }
        if (matricula || nome) entries.push({ matricula, nome });
      }
      if (entries.length === 0) {
        toast.error("Nenhuma linha com nome/matrícula encontrada na planilha");
        setResolving(false);
        return;
      }
      const res = await axios.post(`${API}/admin/epi-records/resolve-list`,
        { entries }, { headers, timeout: 60000 });
      setResolved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao ler a planilha");
    } finally {
      setResolving(false);
    }
  };

  const updateItem = (idx, field, value) =>
    setItems((prev) => prev.map((it, i) =>
      i === idx ? { ...it, [field]: value } : it));
  const addItem = () =>
    setItems((prev) => [...prev, { qtd: 1, descricao: "", ca: "" }]);
  const removeItem = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const toEmit = (resolved?.matched || []).filter((m) => !m.has_record);
  const alreadyHave = (resolved?.matched || []).filter((m) => m.has_record);

  const start = async () => {
    if (!items.some((it) => (it.descricao || "").trim())) {
      toast.error("Informe ao menos 1 EPI");
      return;
    }
    setStarting(true);
    try {
      const r = await axios.post(`${API}/admin/epi-records/bulk-create-by-ids`,
        { employee_ids: toEmit.map((m) => m.id), items,
          cidade_assinatura: cidade }, { headers });
      setJob({ job_id: r.data.job_id, total: r.data.total,
        done: 0, skipped: 0, errors: 0, status: "running" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar emissão");
    } finally {
      setStarting(false);
    }
  };

  const processed = job
    ? job.done + job.skipped + (job.errors || 0) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o && job?.status === "running") return;
      onOpenChange(o);
    }}>
      <DialogContent className="max-w-3xl w-[95vw]" data-testid="epi-excel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
            Emitir Fichas de EPI por planilha (Excel)
          </DialogTitle>
          <DialogDescription>
            Anexe uma planilha com as colunas <b>nome</b> e/ou{" "}
            <b>matrícula</b>. A data de entrega de cada EPI será a{" "}
            <b>data de admissão</b> do próprio funcionário.
          </DialogDescription>
        </DialogHeader>

        {!job ? (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <label className="flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-4 cursor-pointer hover:bg-slate-100">
              <FileSpreadsheet className="w-6 h-6 text-emerald-700" />
              <span className="text-xs font-medium text-slate-700">
                {fileName || "Clique para anexar .xlsx / .xls / .csv"}
              </span>
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden"
                data-testid="epi-excel-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) parseFile(f);
                  e.target.value = "";
                }} />
            </label>

            {resolving && (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                Lendo planilha e localizando funcionários…
              </div>
            )}

            {resolved && (
              <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2"
                data-testid="epi-excel-summary">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                    {toEmit.length} a emitir
                  </Badge>
                  {alreadyHave.length > 0 && (
                    <Badge variant="outline" className="text-amber-700 border-amber-300">
                      {alreadyHave.length} já possuem ficha (serão pulados)
                    </Badge>
                  )}
                  {resolved.unmatched.length > 0 && (
                    <Badge variant="outline" className="text-red-700 border-red-300">
                      {resolved.unmatched.length} não encontrados
                    </Badge>
                  )}
                </div>
                {toEmit.length > 0 && (
                  <div className="max-h-28 overflow-y-auto rounded border border-slate-100 text-[11px]">
                    {toEmit.map((m) => (
                      <div key={m.id} className="flex justify-between px-2 py-1 border-b border-slate-50 last:border-0">
                        <span>{m.nome}</span>
                        <span className="text-slate-500 font-mono">
                          Mat. {m.matricula} · Adm. {m.admissao || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {resolved.unmatched.length > 0 && (
                  <div className="text-[11px] text-red-700">
                    Não encontrados no cadastro:{" "}
                    {resolved.unmatched.map((u) =>
                      u.nome || u.matricula).join("; ")}
                  </div>
                )}
              </div>
            )}

            {resolved && toEmit.length > 0 && (
              <>
                <div className="rounded-md border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                      <Package className="w-3.5 h-3.5" />
                      EPIs Entregues (iguais para todos — data de entrega = admissão)
                    </Label>
                    <Button type="button" size="sm" variant="outline" onClick={addItem}
                      data-testid="epi-excel-add-item">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {items.map((it, idx) => (
                      <div key={idx}
                        className="grid grid-cols-12 gap-2 items-end pb-2 border-b border-slate-100 last:border-0">
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase">Qtd</Label>
                          <Input className="text-xs" type="number" min="1"
                            value={it.qtd}
                            onChange={(e) => updateItem(idx, "qtd", e.target.value)} />
                        </div>
                        <div className="col-span-6">
                          <Label className="text-[10px] uppercase">EPI fornecido</Label>
                          <Input className="text-xs" placeholder="Ex: CAMISA MANGA COMPRIDA"
                            value={it.descricao}
                            onChange={(e) => updateItem(idx, "descricao", e.target.value)}
                            data-testid={`epi-excel-item-${idx}-desc`} />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-[10px] uppercase">C.A.</Label>
                          <Input className="text-xs" placeholder="49490"
                            value={it.ca}
                            onChange={(e) => updateItem(idx, "ca", e.target.value)} />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button type="button" variant="ghost" size="sm"
                            onClick={() => removeItem(idx)}
                            disabled={items.length === 1}>
                            <Trash className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Cidade da assinatura</Label>
                  <Input value={cidade} onChange={(e) => setCidade(e.target.value)}
                    placeholder="Salvador/BA" />
                </div>
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={start}
                disabled={!resolved || toEmit.length === 0 || starting}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="epi-excel-start">
                {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                Emitir {toEmit.length || ""} ficha{toEmit.length === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3" data-testid="epi-excel-progress">
            <div className="text-sm font-medium text-slate-800">
              {job.status === "running" ? "Emitindo fichas…"
                : job.status === "finished" ? "Emissão concluída!"
                : "Emissão com erro"}
            </div>
            <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-600 transition-all"
                style={{ width: `${Math.min(100,
                  Math.round((processed / (job.total || 1)) * 100))}%` }} />
            </div>
            <div className="text-xs text-slate-600">
              {processed} de {job.total} processados — {job.done} emitidas,{" "}
              {job.skipped} já tinham
              {job.errors ? `, ${job.errors} erro(s)` : ""}
            </div>
            {job.status !== "running" && (
              <DialogFooter>
                <Button onClick={() => onOpenChange(false)}
                  data-testid="epi-excel-close">
                  Fechar
                </Button>
              </DialogFooter>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateEpiDialog({ open, onOpenChange, onCreated, adminToken }) {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [cidade, setCidade] = useState("Salvador/BA");
  const [items, setItems] = useState([
    { data_entrega: "", qtd: 1, descricao: "", ca: "", data_devolucao: "" },
  ]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployeeId(""); setSearch(""); setCidade("Salvador/BA");
    setItems([{ data_entrega: "", qtd: 1, descricao: "", ca: "", data_devolucao: "" }]);
    axios.get(`${API}/admin/employees`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      params: { limit: 1000 },
    }).then((r) => {
      setEmployees(Array.isArray(r.data) ? r.data : (r.data?.employees || []));
    }).catch(() => setEmployees([]));
  }, [open, adminToken]);

  const filtered = employees.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (e.nome || "").toLowerCase().includes(q) ||
      (e.matricula || "").toLowerCase().includes(q) ||
      (e.cpf || "").includes(q)
    );
  }).slice(0, 30);

  const selected = employees.find((e) => e.id === employeeId);

  const selectedAdm = selected?.admissao || selected?.data_admissao || "";
  useEffect(() => {
    if (!selectedAdm) return;
    setItems((prev) => prev.map((it) =>
      (it.data_entrega || "").trim()
        ? it : { ...it, data_entrega: selectedAdm }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const updateItem = (idx, key, value) => {
    setItems((prev) => prev.map((it, i) =>
      i === idx ? { ...it, [key]: value } : it,
    ));
  };
  const addItem = () => setItems((prev) => [...prev,
    { data_entrega: "", qtd: 1, descricao: "", ca: "", data_devolucao: "" }]);
  const removeItem = (idx) => setItems((prev) =>
    prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!employeeId) return toast.error("Selecione um funcionário");
    const validItems = items.filter((it) => (it.descricao || "").trim());
    if (validItems.length === 0)
      return toast.error("Adicione ao menos 1 EPI com descrição");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/epi-records/create`,
        { employee_id: employeeId, items: validItems,
          cidade_assinatura: cidade.trim() },
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 30000 },
      );
      toast.success("Ficha de EPI criada com sucesso");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar ficha");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw]" data-testid="epi-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardHat className="w-5 h-5 text-amber-700" />
            Nova Ficha de EPI
          </DialogTitle>
          <DialogDescription>
            Registre os EPIs entregues ao colaborador. A ficha será
            disponibilizada para assinatura eletrônica.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          {/* Employee picker */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <Label className="text-xs font-semibold text-slate-700">
              Funcionário
            </Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nome, matrícula ou CPF…"
                className="pl-9 bg-white"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="epi-employee-search"
              />
            </div>
            {selected ? (
              <div className="flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 p-2">
                <div className="text-xs">
                  <div className="font-semibold text-emerald-900">{selected.nome}</div>
                  <div className="text-emerald-700 font-mono">
                    Mat. {selected.matricula} · CPF {selected.cpf}
                  </div>
                </div>
                <button onClick={() => setEmployeeId("")}
                  className="text-emerald-700 hover:text-emerald-900">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="max-h-32 overflow-y-auto rounded-md border border-slate-200 bg-white">
                {filtered.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">
                    {search ? "Nenhum encontrado." : "Carregando…"}
                  </div>
                ) : filtered.map((e) => (
                  <button key={e.id} onClick={() => setEmployeeId(e.id)}
                    className="w-full text-left p-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 text-xs">
                    <div className="font-medium">{e.nome}</div>
                    <div className="text-slate-500 font-mono">
                      Mat. {e.matricula} · CPF {e.cpf}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Items table */}
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Package className="w-3.5 h-3.5" />
                EPIs Entregues
              </Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem}
                data-testid="epi-add-item-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx}
                  className="grid grid-cols-12 gap-2 items-end pb-2 border-b border-slate-100 last:border-0">
                  <div className="col-span-3">
                    <Label className="text-[10px] uppercase">Data entrega</Label>
                    <Input className="text-xs" placeholder="DD/MM/AAAA"
                      value={it.data_entrega}
                      onChange={(e) => updateItem(idx, "data_entrega", e.target.value)}
                      data-testid={`epi-item-${idx}-date`} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[10px] uppercase">Qtd</Label>
                    <Input className="text-xs" type="number" min="1"
                      value={it.qtd}
                      onChange={(e) => updateItem(idx, "qtd", e.target.value)}
                      data-testid={`epi-item-${idx}-qtd`} />
                  </div>
                  <div className="col-span-5">
                    <Label className="text-[10px] uppercase">EPI fornecido</Label>
                    <Input className="text-xs" placeholder="Ex: CAMISA MANGA COMPRIDA"
                      value={it.descricao}
                      onChange={(e) => updateItem(idx, "descricao", e.target.value)}
                      data-testid={`epi-item-${idx}-desc`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase">C.A.</Label>
                    <Input className="text-xs" placeholder="49490"
                      value={it.ca}
                      onChange={(e) => updateItem(idx, "ca", e.target.value)}
                      data-testid={`epi-item-${idx}-ca`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      data-testid={`epi-item-${idx}-remove`}>
                      <Trash className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Cidade da assinatura</Label>
            <Input value={cidade} onChange={(e) => setCidade(e.target.value)}
              placeholder="Salvador/BA" data-testid="epi-cidade-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={busy}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="epi-create-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <HardHat className="w-4 h-4 mr-2" />}
            Gerar ficha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminEpiRecordsTab({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/epi-records`, {
        headers: { Authorization: `Bearer ${adminToken}` }, timeout: 15000,
      });
      setRows(r.data || []);
    } catch { toast.error("Falha ao carregar"); }
    finally { setLoading(false); }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (r.employee_nome || "").toLowerCase().includes(q) ||
      (r.employee_matricula || "").toLowerCase().includes(q)
    );
  });

  const openPreview = async (row) => {
    try {
      const r = await axios.get(
        `${API}/admin/epi-records/${row.id}/preview`,
        { headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob", timeout: 30000 },
      );
      setPreviewUrl(URL.createObjectURL(r.data));
      setPreviewName(`Ficha de EPI — ${row.employee_nome || row.id.slice(0, 8)}`);
    } catch { toast.error("Não foi possível abrir o PDF"); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(""); setPreviewName("");
  };

  const remove = async (row) => {
    if (!window.confirm(`Excluir ficha de ${row.employee_nome}?`)) return;
    try {
      await axios.delete(`${API}/admin/epi-records/${row.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Ficha excluída"); load();
    } catch { toast.error("Falha ao excluir"); }
  };

  const pending = rows.filter((r) => r.status !== "signed").length;
  const signed = rows.filter((r) => r.status === "signed").length;

  return (
    <div className="space-y-4" data-testid="admin-epi-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <HardHat className="w-5 h-5 text-amber-700" />
            Fichas de EPI
          </h2>
          <p className="text-xs text-slate-500">
            Controle de fornecimento de Equipamentos de Proteção Individual (NR-6).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}
            disabled={loading} data-testid="epi-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <RefreshCw className="w-4 h-4 mr-2" />}
            Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}
            data-testid="epi-bulk-upload-btn">
            <FileUp className="w-4 h-4 mr-2" />
            Importar PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExcelOpen(true)}
            className="text-emerald-800 border-emerald-300 hover:bg-emerald-50"
            data-testid="epi-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Emitir por Planilha
          </Button>
          <Button variant="outline" size="sm" onClick={() => setContractOpen(true)}
            className="text-amber-800 border-amber-300 hover:bg-amber-50"
            data-testid="epi-contract-btn">
            <Building2 className="w-4 h-4 mr-2" />
            Emitir por Contrato
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="epi-new-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Ficha
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[10px] text-slate-500 uppercase font-bold">Total</div>
          <div className="text-2xl font-bold text-slate-900">{rows.length}</div>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] text-amber-700 uppercase font-bold">Pendentes</div>
          <div className="text-2xl font-bold text-amber-900">{pending}</div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] text-emerald-700 uppercase font-bold">Assinadas</div>
          <div className="text-2xl font-bold text-emerald-900">{signed}</div>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Filtrar por nome ou matrícula…" className="pl-9"
          value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">Funcionário</th>
              <th className="px-3 py-2 text-center">Itens</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-center">Criada em</th>
              <th className="px-3 py-2 text-center">Assinada em</th>
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                  {rows.length === 0
                    ? 'Nenhuma ficha. Clique em "Nova Ficha" acima.'
                    : "Nenhuma ficha corresponde ao filtro."}
                </td>
              </tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{r.employee_nome || "—"}</div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    Mat. {r.employee_matricula || "—"}
                  </div>
                </td>
                <td className="px-3 py-2 text-center">
                  <Badge variant="outline" className="text-amber-700 border-amber-300">
                    {r.items_count} EPIs
                  </Badge>
                </td>
                <td className="px-3 py-2 text-center">
                  {r.status === "signed" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Assinada
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 border-amber-300">
                      <Clock className="w-3 h-3 mr-1" />
                      Pendente
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  {fmtDateTime(r.created_at)}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  {r.signed_at ? fmtDateTime(r.signed_at) : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openPreview(r)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!previewUrl} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewName}</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe src={previewUrl} title="EPI preview"
              className="flex-1 w-full rounded-md border border-slate-200" />
          )}
        </DialogContent>
      </Dialog>

      <BulkEpiContractDialog open={contractOpen} onOpenChange={setContractOpen}
        onDone={load} adminToken={adminToken} />
      <BulkEpiExcelDialog open={excelOpen} onOpenChange={setExcelOpen}
        onDone={load} adminToken={adminToken} />
      <CreateEpiDialog open={createOpen} onOpenChange={setCreateOpen}
        onCreated={load} adminToken={adminToken} />

      <BulkDocsUploadDialog open={bulkOpen} onOpenChange={setBulkOpen}
        kind="epi" adminToken={adminToken}
        title="Importar Fichas de EPI do Folhamatic"
        accentClassName="bg-amber-700 hover:bg-amber-800"
        onConfirmed={load} />
    </div>
  );
}
